import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  ArrowRight, Scales, MapPin, Detective, List,
  Buildings, UsersThree, Briefcase,
} from "@phosphor-icons/react";
import heroCover from "@/assets/hero-candidate.png";
import homeStory from "@/assets/home-story.png";
import howItWorks from "@/assets/how-it-works.png";

const AUDIENCES = [
  { to: "/register", icon: Briefcase, tag: "Job seekers", title: "I'm looking for work",
    body: "Find jobs you can realistically apply for — eligibility checked before any match score.",
    cta: "Find jobs I can apply for", primary: true },
  { to: "/for-employers", icon: Buildings, tag: "Employers", title: "I'm hiring",
    body: "Fewer irrelevant applications. Reach candidates who actually meet your real requirements.",
    cta: "Find candidates who qualify" },
  { to: "/for-recruiters", icon: UsersThree, tag: "Recruiters", title: "I'm recruiting",
    body: "Match client roles against candidate pools without drowning in CVs.",
    cta: "Match candidates to roles" },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-sm bg-slate-900 flex items-center justify-center text-white font-heading font-black text-sm">E</span>
            <span className="font-heading font-bold tracking-tight text-lg">Eligvia</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/pricing" className="hidden sm:block"><Button variant="ghost" data-testid="header-pricing-btn" className="text-slate-700">Pricing</Button></Link>
            <Link to="/help" className="hidden sm:block"><Button variant="ghost" data-testid="header-help-btn" className="text-slate-700">Help</Button></Link>
            <Link to="/login"><Button variant="ghost" data-testid="header-login-btn" className="text-slate-700 px-3">Sign in</Button></Link>
            <Link to="/register"><Button data-testid="header-register-btn" className="bg-slate-900 hover:bg-slate-800 px-3">Create account</Button></Link>
            <MobileMenu />
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 pt-16 pb-8">
        <div className="max-w-3xl">
          <p className="font-mono-num text-xs uppercase tracking-[0.2em] text-slate-500 mb-5">Eligibility first · Matching second</p>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            The right jobs, people<br />and requirements.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
            Eligvia connects the right jobs, people and hiring requirements — before the application noise begins. It checks who can realistically apply, then explains every decision.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/register"><Button data-testid="hero-register-btn" className="bg-slate-900 hover:bg-slate-800 gap-2">Find jobs I can apply for <ArrowRight weight="bold" /></Button></Link>
            <Link to="/pricing"><Button variant="outline" data-testid="hero-pricing-btn" className="border-slate-300 text-slate-700">See pricing</Button></Link>
          </div>
        </div>
        <div className="mt-10">
          <img src={heroCover} data-testid="hero-cover-image"
               alt="From confusion and unclear eligibility to a shortlist of realistic matches — eligible roles with a criteria-fit score and clear reasons"
               className="w-full rounded-xl border border-slate-200 shadow-sm" />
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-6 py-10">
        <img src={howItWorks} data-testid="how-it-works-image"
             alt="How it works: from requirements to the right match — profile and role requirements, an eligibility check, fit matching, then an explainable result"
             className="w-full rounded-xl border border-slate-200 shadow-sm" />
      </section>

      {/* Three audiences */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {AUDIENCES.map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.to} to={a.to} data-testid={`audience-${a.tag.toLowerCase().split(" ")[0]}`}
                className={`group rounded-lg border p-6 transition-colors ${a.primary ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                <div className={`h-11 w-11 rounded-md flex items-center justify-center ${a.primary ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"}`}>
                  <Icon weight="duotone" size={24} />
                </div>
                <p className={`mt-4 text-xs font-mono-num uppercase tracking-wide ${a.primary ? "text-slate-300" : "text-slate-400"}`}>{a.tag}</p>
                <h3 className="mt-1 font-heading font-bold text-xl">{a.title}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${a.primary ? "text-slate-300" : "text-slate-600"}`}>{a.body}</p>
                <span className={`mt-4 inline-flex items-center gap-1.5 text-sm font-semibold ${a.primary ? "text-white" : "text-slate-900"}`}>
                  {a.cta} <ArrowRight weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* From overwhelm to the right fit */}
      <section className="mx-auto max-w-6xl px-6 pb-12">
        <img src={homeStory} data-testid="home-story-image"
             alt="From too many jobs and not enough clarity to the right people and a brighter tomorrow — eligibility-first matching for job seekers and employers"
             className="w-full rounded-xl border border-slate-200 shadow-sm" />
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Scales, title: "Eligibility before fit", body: "A 95% skills match is irrelevant if you legally or practically cannot apply. Hard blockers come first." },
            { icon: Detective, title: "Every decision explained", body: "See exactly why a job is eligible, possibly eligible, or not — traced to the employer's own wording." },
            { icon: MapPin, title: "Unknown stays unknown", body: "We never turn a missing fact into a rejection. Ambiguous roles are flagged for verification, not hidden." },
          ].map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title}>
                <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center text-slate-800 mb-4"><Icon weight="duotone" size={22} /></div>
                <h3 className="font-heading font-bold text-lg">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-8 text-sm text-slate-500 flex flex-col sm:flex-row justify-between gap-3">
          <span>© 2026 Eligvia. A job discovery, eligibility & matching platform.</span>
          <div className="flex items-center gap-4">
            <Link to="/help" className="hover:text-slate-900 underline" data-testid="footer-help">Help</Link>
            <Link to="/privacy" className="hover:text-slate-900 underline" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900 underline" data-testid="footer-terms">Terms</Link>
            <Link to="/rules" className="hover:text-slate-900 underline" data-testid="footer-rules">User Rules</Link>
            <Link to="/legal" className="hover:text-slate-900 underline" data-testid="footer-legal">Legal</Link>
            <span className="font-mono-num text-xs">Facts before assumptions.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

const MOBILE_LINKS = [
  { to: "/for-employers", label: "For employers" },
  { to: "/for-recruiters", label: "For recruiters" },
  { to: "/pricing", label: "Pricing" },
  { to: "/help", label: "Help Center" },
  { to: "/rules", label: "User Rules" },
];

function MobileMenu() {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="sm:hidden h-10 w-10" aria-label="Menu" data-testid="landing-mobile-menu-btn"><List size={22} /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" data-testid="landing-mobile-menu">
        {MOBILE_LINKS.map((l) => (
          <DropdownMenuItem key={l.to} onSelect={() => navigate(l.to)} className="py-2.5" data-testid={`mobile-menu-${l.to.slice(1)}`}>{l.label}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
